import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, UserPlus, Save, Shield } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PASSWORD_INPUT_PROPS } from "@/utils/passwordInputProps";
import { ROLE_OPTIONS } from "./roleLabels";

/**
 * Enterprise-styled create/edit user modal. Reused by the Security
 * Administration page. All layout tokens match the master/detail
 * layout so the modal feels part of the console rather than a
 * bolt-on dialog.
 */
export default function UserFormModal({ open, mode, user = null, onCancel, onSaved }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [mobile, setMobile] = useState("");
    const [role, setRole] = useState("customer");
    const [password, setPassword] = useState("");
    const [isSecAdmin, setIsSecAdmin] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (mode === "edit" && user) {
            setName(user.name || "");
            setEmail(user.email || "");
            setMobile(user.mobile || "");
            setRole(user.role || "customer");
            setIsSecAdmin(!!user.is_security_admin);
            setPassword("");
        } else {
            setName(""); setEmail(""); setMobile("");
            setRole("customer"); setPassword(""); setIsSecAdmin(false);
        }
    }, [open, mode, user]);

    // Escape closes the modal — a11y expected behaviour for a dialog.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel]);

    if (!open) return null;

    const isCreate = mode === "create";
    const canSubmit =
        name.trim() && email.trim() && (isCreate ? password.length >= 12 : true) && !busy;

    async function submit() {
        setBusy(true);
        try {
            if (isCreate) {
                await api.adminCreateUser({
                    name: name.trim(), email: email.trim(),
                    role, mobile: mobile.trim() || null,
                    temporary_password: password,
                    is_security_admin: isSecAdmin,
                    force_password_change: true,
                });
                toast.success("User created");
            } else {
                await api.adminPatchUser(user.id, {
                    name: name.trim(), email: email.trim(),
                    role, mobile: mobile.trim() || null,
                });
                toast.success("User updated");
            }
            await onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    }

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid={isCreate ? "admin-sec-create-modal" : "admin-sec-edit-modal"}
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between bg-white">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-md bg-[#E1E6F0] flex items-center justify-center text-[#131B2F]">
                            {isCreate ? <UserPlus size={16} strokeWidth={1.75} /> : <Save size={16} strokeWidth={1.75} />}
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5565]">
                                {isCreate ? "Create" : "Update"} user
                            </div>
                            <div className="text-base font-semibold text-[#131B2F]">
                                {isCreate ? "New user account" : user?.name || user?.email || "Edit user"}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Full name" required>
                            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-sec-form-name" className={inputCls} />
                        </Field>
                        <Field label="Email" required>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-sec-form-email" className={inputCls} />
                        </Field>
                        <Field label="Mobile">
                            <input value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="admin-sec-form-mobile" placeholder="Optional" className={inputCls} />
                        </Field>
                        <Field
                            label="Account role"
                            required
                            hint="Controls access to the app. Resource type is managed in Field Team Management."
                        >
                            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-sec-form-role" className={inputCls}>
                                {ROLE_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    {isCreate && (
                        <>
                            <Field label="Temporary password" required hint="≥ 12 chars, upper/lower/number/special. User must change on first login.">
                                <input
                                    type="text"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="e.g. Autumn!Solar2026"
                                    data-testid="admin-sec-form-password"
                                    autoComplete="new-password"
                                    {...PASSWORD_INPUT_PROPS}
                                    className={`${inputCls} font-mono`}
                                />
                            </Field>
                            <label className="flex items-start gap-2.5 p-3 rounded-md bg-[#E1E6F0]/40 border border-[#E1E6F0] text-sm text-[#131B2F]">
                                <input
                                    type="checkbox"
                                    checked={isSecAdmin}
                                    onChange={(e) => setIsSecAdmin(e.target.checked)}
                                    data-testid="admin-sec-form-secadmin"
                                    className="h-4 w-4 mt-0.5"
                                />
                                <span>
                                    <span className="inline-flex items-center gap-1.5 font-medium">
                                        <Shield size={13} /> Grant Security Administrator privilege
                                    </span>
                                    <div className="mt-0.5 text-[11px] text-[#4B5565]">
                                        Adds full access to this Security Administration console, including user, session and audit management.
                                    </div>
                                </span>
                            </label>
                        </>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                        data-testid="admin-sec-form-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0B1220] text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2"
                        data-testid="admin-sec-form-submit"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {isCreate ? "Create user" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls =
    "w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-sm outline-none focus:border-[#3E9E2E] focus:ring-1 focus:ring-[#3E9E2E]/30 bg-white";

function Field({ label, required = false, hint = null, children }) {
    return (
        <div>
            <label className="block text-[11px] text-[#4B5565] uppercase tracking-[0.1em] mb-1">
                {label}
                {required && <span className="text-[#DA3535] ml-0.5">*</span>}
            </label>
            {children}
            {hint && <div className="mt-1 text-[11px] text-[#4B5565]">{hint}</div>}
        </div>
    );
}
